import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { company } from '~/company/Company';
import type { CloudflareResizeOptions } from '~/components/CloudflareResponsiveImage/CloudflareResizeOptions';
import CloudflareResponsiveImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import Icon from '~/components/Icon/Icon';
import { SimpleCounter } from '~/components/isolated/SimpleCounter';
import { ModalSaveMore } from '~/components/ModalSaveMore';
import MultibuyLevels from '~/components/MultibuyLevels';
import { formatVolume } from '~/components/ProductCard/utils';
import { useProductCardV3 } from '~/components/ProductCardV3/hooks/useProductCardV3';
import ProductInfoC from '~/components/ProductCardV3/shared/ProductInfoC';
import type { ProductCardVariantProps } from '~/components/ProductCardV3/types';
import { useGlobal } from '~/hooks/useGlobal';
import { calcDiscountPercentage } from '~/utils/formaters';
import {
  getApplicableMultibuyLevel,
  getLowestMultibuyLevel,
  sortMultibuyLevels,
} from '~/utils/multibuy';

import styles from './ProductCardC.module.scss';

/**
 * ProductCardC - Horizontal cart product card (full width × 118px mobile, 160px web)
 * Designed for shopping cart with selection checkbox
 */
const ProductCardC = observer(
  ({
    product,
    className,
    productState,
    isSelected = false,
    onSelectionChange,
    disabled = false,
  }: ProductCardVariantProps) => {
    const { isMobile } = useGlobal();
    const { t } = useTranslation();

    const [isShowModalSaveMore, setIsShowModalSaveMore] = useState(false);

    const {
      currentItem,
      limit,
      limitReachedText,
      image,
      oldPrice,
      currentPrice,
      cartItemsCount,
      multiBuyInfo,
      changeCount,
      totalCartPrice,
      isMultiBuy,
      isMultiBuyActive,
    } = useProductCardV3(product, productState?.selectedOffer, 'c');

    const volume = formatVolume(currentItem.properties);
    const hasDiscount = oldPrice && oldPrice !== currentPrice;

    const discountPercent = useMemo(() => {
      if (!hasDiscount || !currentItem.price || !currentItem.discountPrice) {
        return null;
      }

      return (
        calcDiscountPercentage(currentItem.price, currentItem.discountPrice) ||
        null
      );
    }, [hasDiscount, currentItem.price, currentItem.discountPrice]);

    const handleShowModalSaveMore = useCallback(() => {
      setIsShowModalSaveMore(true);
    }, [setIsShowModalSaveMore]);

    const handleCloseModalSaveMore = useCallback(() => {
      setIsShowModalSaveMore(false);
    }, [setIsShowModalSaveMore]);

    const handleCheckboxChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onSelectionChange?.(e.target.checked);
      },
      [onSelectionChange],
    );

    const handleIncrement = useCallback(() => {
      changeCount(cartItemsCount + 1, 'add');
    }, [cartItemsCount, changeCount]);

    const handleDecrement = useCallback(() => {
      changeCount(cartItemsCount - 1, 'remove');
    }, [cartItemsCount, changeCount]);

    const imageSize = useMemo<CloudflareResizeOptions>(() => {
      return isMobile
        ? {
            width: 72,
            height: 72,
          }
        : {
            width: 144,
            height: 144,
          };
    }, [isMobile]);

    const multibuyBadge = useMemo(() => {
      if (
        !multiBuyInfo ||
        !isMultiBuy ||
        !currentItem.multibuyPriceLevels?.length
      ) {
        return null;
      }

      const sortedLevels = sortMultibuyLevels(currentItem.multibuyPriceLevels);
      const applicableLevel = getApplicableMultibuyLevel(
        sortedLevels,
        cartItemsCount,
      );

      // Find next unreached level if multibuy is not active
      const nextLevel = !isMultiBuyActive
        ? sortedLevels.find((level) => cartItemsCount < level.quantity)
        : null;

      const lowestLevel = getLowestMultibuyLevel(sortedLevels);
      const levelToDisplay = applicableLevel || nextLevel || lowestLevel;

      if (!levelToDisplay) {
        return null;
      }

      if (!multiBuyInfo.remainingCount || !multiBuyInfo.nextDiscountPercent) {
        return null;
      }

      return (
        <span className={styles.multibuyBadge}>
          {cartItemsCount === 0
            ? t('multibuyOfferNoCartValue', {
                count: levelToDisplay.quantity,
                value: multiBuyInfo.discountValue,
              })
            : multiBuyInfo.remainingCount && multiBuyInfo.nextDiscountPercent
              ? t('multibuyOfferWithCartValue', {
                  count: Math.max(0, multiBuyInfo.remainingCount),
                  value: multiBuyInfo.nextDiscountValue,
                })
              : null}
          <Icon type="info" size={16} onClick={handleShowModalSaveMore} />
        </span>
      );
    }, [
      multiBuyInfo,
      isMultiBuy,
      isMultiBuyActive,
      currentItem.multibuyPriceLevels,
      cartItemsCount,
      isMobile,
      t,
    ]);

    return (
      <>
        <div
          className={classes([
            styles.card,
            disabled && styles.disabled,
            className,
          ])}
          data-company={company.name}
        >
          <div className={styles.imageContainer}>
            <Link
              to={
                currentItem.sku
                  ? `/p/${product.slug}?sku=${encodeURIComponent(currentItem.sku)}`
                  : `/p/${product.slug}`
              }
              className={styles.imageLink}
              data-link={`productCard:open-${currentItem.sku}`}
            >
              <CloudflareResponsiveImage
                alt={currentItem.name}
                src={image}
                srcParams={imageSize}
                className={styles.image}
              />
            </Link>
          </div>

          <div className={styles.contentContainer}>
            <ProductInfoC
              name={currentItem.name}
              productSlug={product.slug}
              currentSku={currentItem.sku ?? ''}
              isSelected={isSelected}
              onSelectionChange={handleCheckboxChange}
            />

            <div className={styles.badgesRow}>
              <span>{volume}</span>
              {multibuyBadge && (
                <>
                  <span className={styles.divider}>•</span>
                  {multibuyBadge}
                </>
              )}
            </div>

            <div className={styles.bottomRow}>
              <div className={styles.priceContainer}>
                {hasDiscount && !isMultiBuy && (
                  <div className={styles.discountRow}>
                    <div className={styles.oldPrice}>{oldPrice}</div>
                    {discountPercent && (
                      <div className={styles.discountPercent}>
                        -{discountPercent}%
                      </div>
                    )}
                  </div>
                )}

                <div
                  className={classes([
                    styles.currentPrice,
                    hasDiscount && styles.currentPrice_discounted,
                  ])}
                >
                  {currentPrice} / {t('item')}
                </div>
              </div>

              {cartItemsCount > 0 && (
                <div className={styles.counter}>
                  <SimpleCounter
                    count={cartItemsCount}
                    onIncrement={handleIncrement}
                    onDecrement={handleDecrement}
                    size={isMobile ? 'M' : 'XL'}
                    variant="primary"
                    colorVariant="outlined"
                    limit={limit}
                    limitReachedText={limitReachedText}
                    totalPrice={
                      !isMobile && totalCartPrice ? totalCartPrice : undefined
                    }
                  />
                </div>
              )}
            </div>
          </div>

          {disabled && <div className={styles.disabledOverlay} />}
        </div>

        <ModalSaveMore
          isShow={isShowModalSaveMore}
          onBackdropDismiss={handleCloseModalSaveMore}
        >
          <MultibuyLevels
            levels={currentItem.multibuyPriceLevels}
            count={cartItemsCount}
            basePrice={currentItem.price}
          />
        </ModalSaveMore>
      </>
    );
  },
);

export default ProductCardC;
